export const EMAIL_REGEX = /^.+@.+\..+$/

export function stripPhoneNumber(phoneNumber) {
  const NON_NUMERIC = /[^0-9]+/g;
  return phoneNumber.replace(NON_NUMERIC, '').substr(0, 10)
}

/**
 * Formats a phone number string, using either periods or the standard
 * parentheses and hyphens to delimit the different number sections.
 * Accepts an incomplete phone number too, so that it can be used to format
 * a partial phone number as the user is entering their number into a form.
 * When an incomplete phone number is provided, the delimiter isn't added
 * unless the character *after* the delimiter is also part of the string;
 * this ensures that the user can press backspace while entering their input
 * into the form and the delimiter is deleted as expected.
 *
 * @param {string} str The (maybe incomplete) phone number, maybe w/ delimiters.
 * @param {Object} options
 * @param {string} [options.delimiterType=standard] The string 'standard' or
 *   'periods', to pick whether the parts will be separated by periods or by
 *   parentheses and a hyphen.
 * @param  {boolean} [options.omitEndingDelimiter=false] If the formatted string
 *   would end with a delimeter (e.g., inputting "310" would be formatted to
 *   "(310)", which ends with the delimiter ")"), whether to omit that final
 *   delimiter. This is needed when formatting an incomplete phone number from
 *   user input, to handle the user pressing backspace. For example, if they type
 *   "310" and the result is "(310)" and then they press delete, the next result
 *   has to be "(310" or their delete won't work at all.
 * @return {String} The formatted (partial) phone number.
 */
export function formatPhoneNumber(str, {delimiterType='standard', omitEndingDelimiter=false} = {}) {
  let clean = stripPhoneNumber(str)
  let formatted = ''
  let delimiters = delimiterType === 'standard' ? ['(', ') ', '–'] : ['', '.', '.'];

  if (clean.length > 0)
    formatted += delimiters[0] + clean.substr(0, 3)

  if (clean.length >= 3)
    formatted +=
      (clean.length === 3 && omitEndingDelimiter ? '' : delimiters[1]) +
      clean.substr(3, 3)

  if (clean.length >= 6)
    formatted +=
      (clean.length === 6 && omitEndingDelimiter ? '' : delimiters[2]) +
      clean.substr(6, 4);

  return formatted;
}

/**
 * Parses a URL's query params into an objcet with each parameter as a key.
 * Uses a simplified version of the code in https://github.com/sindresorhus/query-string
 * (We don't use that module directly because we don't need to support
 * non-string query parameter values with the made up [] syntax or key sorting.)
 *
 * @param {string} search The search portion of a URL (often location.search)
 * @return {object} The parsed parameters.
 */
export function parseSearchString(search) {
  // strip leading ? and split by & *before* url decoding,
  // so that encoded &'s don't count. Then, reduce to an object.
  return search.substring(1).split('&').reduce((result, keyValuePair) => {
    // Because of an old firefox bug, we can't just do
    // var [key, val] = keyValuePair.split('=');
    // See https://github.com/sindresorhus/query-string/pull/37
    var parts = keyValuePair.replace(/\+/g, ' ').split('=');
    var key = parts.shift();
    var val = parts.length > 0 ? parts.join('=') : undefined;

    // missing `=` should be `null`:
    // http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
    val = val === undefined ? null : decodeURIComponent(val);

    result[decodeURIComponent(key)] = val;
    return result;

  }, Object.create(null));
}

/**
 * Takes an array of items and partitions those items based on the return value
 * of function run over each item (or, for convenience, a string property name).
 * Returns an object where the keys are (the stringified version of) the value
 * that each object in the partition matched on. The value at each key is an
 * array of items.
 */
export function partition(fnOrKey, items) {
  const partitionFn = typeof fnOrKey === 'function' ? fnOrKey : (it) => it[fnOrKey];

  return items.reduce((acc, item) => {
    const partitionName = partitionFn(item);
    acc[partitionName] = acc[partitionName] || [];
    acc[partitionName].push(item);
    return acc;
  }, {});
}

export function arrayToObject(keyBy, arr) {
  const keyByFn = typeof keyBy === 'function' ? keyBy : (it) => it[keyBy];

  return arr.reduce((acc, item) => {
    acc[keyByFn(item)] = item;
    return acc;
  }, {});
}

export function objectValues(obj) {
  return typeof Object.values === "function" ?
    Object.values(obj) :
    Object.keys(obj).reduce((acc, k) => { acc.push(obj[k]); return acc; }, []);
}

export function getPageTitle(component, props) {
  return component.pageTitle(
    props.listings,
    {location: props.location, params: props.params}
  );
}


// WEBPACK FOOTER //
// ./client/utility/utility.js